import React from 'react';
import { createFilterOptions } from '@mui/material';

import { StyledAutocomplete, StyledTextField } from 'components/Input/styles';

import { TimezoneOption, timezoneOption, timezoneOptions } from 'utils/formatters/timezone';

export interface TimezonePickerProps {
  value: string | null;
  onChange: (zone: string) => void;
  ariaLabel: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  inputRef?: React.Ref<HTMLInputElement>;
  'data-testid'?: string;
}

const filterOptions = createFilterOptions<TimezoneOption>({ stringify: (option) => option.searchText });

const TimezonePicker: React.FunctionComponent<TimezonePickerProps> = ({
  value,
  onChange,
  ariaLabel,
  disabled,
  error,
  helperText,
  inputRef,
  'data-testid': dataTestId,
}) => {
  const listed = timezoneOptions();
  const { selected, options } = React.useMemo(() => {
    const listedValue = value ? listed.find((option) => option.zone === value) : undefined;
    if (listedValue || !value) return { selected: listedValue ?? null, options: listed };
    const unlisted = timezoneOption(value);
    return { selected: unlisted, options: [unlisted, ...listed] };
  }, [value, listed]);

  const handleChange = (_: React.SyntheticEvent, option: TimezoneOption | null): void => {
    if (option && option.zone !== value) {
      onChange(option.zone);
    }
  };

  return (
    <StyledAutocomplete<TimezoneOption, false, boolean, false>
      fullWidth
      size="small"
      disableClearable
      disabled={disabled}
      options={options}
      value={selected}
      onChange={handleChange}
      filterOptions={filterOptions}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, current) => option.zone === current.zone}
      data-testid={dataTestId}
      renderInput={(params) => (
        <StyledTextField
          {...params}
          error={error}
          helperText={helperText}
          inputRef={inputRef}
          inputProps={{ ...params.inputProps, 'aria-label': ariaLabel }}
        />
      )}
    />
  );
};

export default TimezonePicker;
